import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";

import { useSettingsContext } from "../../../../settings/context";
import { apiFetch } from "../../../../utils";
import {
  type DataFormMutationVariables,
  useDataFormContext,
} from "../../../data-form";
import { useDataTableContext } from "../../context";
import { useApplyPreferenceProfile } from "./hooks/apply-preference-profile";
import { useGetProfileFromPreferences } from "./hooks/get-profile-from-preference";
import {
  type DataTablePreferencesBrowseType,
  dataTablePreferencesBrowseSchema,
} from "./schema";
import { createDataTablePreferencesApi, viewPreferencesKey } from "./utils";

export function useAddDataTablePreferenceMutation() {
  const queryClient = useQueryClient();

  const table = useDataTableContext();
  const { api, tableId } = table;

  return useMutation({
    mutationFn: async (name: string) => {
      const tableState = table.getState();

      const preferenceObject = {
        columnFilters: tableState.columnFilters,
        columnOrder: tableState.columnOrder,
        columnPinning: tableState.columnPinning,
        columnSizing: tableState.columnSizing,
        columnVisibility: tableState.columnVisibility,
        sorting: tableState.sorting,
      };

      return apiFetch({
        url: createDataTablePreferencesApi(api),
        schema: dataTablePreferencesBrowseSchema,
        method: "POST",
        data: {
          name,
          tableId,
          settings: JSON.stringify(preferenceObject),
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: viewPreferencesKey(tableId),
      });
    },
  });
}

export function useDataTablePreferenceFormMutation() {
  const queryClient = useQueryClient();
  const { api, tableId } = useDataTableContext();

  const applyPreferenceProfile = useApplyPreferenceProfile();

  const profile = useGetProfileFromPreferences();

  return useMutation({
    mutationFn: async ({
      values,
      type,
    }: DataFormMutationVariables<DataTablePreferencesBrowseType>) => {
      return apiFetch({
        method: type === "update" ? "PUT" : "POST",
        url:
          type === "update"
            ? `${createDataTablePreferencesApi(api)}/${values.id}`
            : createDataTablePreferencesApi(api),
        data: values,
        schema: dataTablePreferencesBrowseSchema,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: viewPreferencesKey(tableId) });

      if (profile?.id === variables.values.id) {
        void applyPreferenceProfile({
          id: variables.values.id,
          name: variables.values.name,
        });
      }
    },
  });
}

export function useDeleteDataTablePreferenceMutation() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { entity, api } = useDataFormContext<
    DataTablePreferencesBrowseType,
    DataTablePreferencesBrowseType
  >();

  const { preferences } = useSettingsContext();
  const table = useDataTableContext();

  return useMutation({
    mutationFn: async () => {
      if (!entity) {
        return;
      }

      return apiFetch({
        method: "DELETE",
        url: `${api}/${entity.id}`,
        errorMessage: intl.formatMessage({
          id: "data-form.delete.error",
          defaultMessage: "Nepodařilo se smazt záznam.",
        }),
      });
    },
    onSuccess: async () => {
      if (entity?.tableId) {
        queryClient.invalidateQueries({
          queryKey: viewPreferencesKey(entity.tableId),
        });
      }

      await preferences.set({
        key: table.tableId,
        preferences: {
          version: table.version,
          profile: null,
        },
      });

      await preferences.refresh();
    },
  });
}
